/**
 * Alert Center — one inbox for every alert (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md, WS-I).
 *
 * Views (Needs attention · Critical · Assigned to me · All open · Resolved · All),
 * filters (area, source, severity, status, assignee, search, window) kept in the
 * URL so any filtered view can be bookmarked, grouped rows (repeat firings
 * collapse with a count), bulk actions, and a "Sources" panel that shows each
 * source's health — including writers that have gone quiet.
 *
 * Endpoints:
 *   GET  /api/admin/alerts?view=&area=&source=&severity=&status=&assignee=&q=&since_days=
 *   GET  /api/admin/alerts/assignees
 *   POST /api/admin/alerts/actions  { ids, action, assignee_admin_id?, note? }
 *   GET  /api/admin/auth/me          (to hide actions from read-only admins)
 */
import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle, BellRing, Check, ChevronDown, ChevronRight, CircleSlash, Database, ExternalLink,
  RefreshCw, RotateCcw, Search, UserPlus, MessageSquare, Bookmark,
} from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { adminFetch, adminMutate } from '../../../utils/adminApi';
import { AdminMe, adminCan, errorMessage } from '../settings/settingsLogic';
import { MeProfile, SavedAlertView, viewNameError } from '../me/meLogic';
import {
  AlertItem, AlertsResponse, Filters, SEVERITY_STYLE, STATUS_LABELS, VIEW_LABELS, View,
  ago, buildQuery, filtersFromSearch, isQuiet, memberIds,
} from './alertsLogic';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 20_000, refetchOnWindowFocus: false } },
});

const VIEW_ORDER: View[] = ['needs_attention', 'critical', 'mine', 'open', 'resolved', 'all'];

interface Assignee { id: string; email: string; full_name: string | null }

const AlertsInner: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useMemo(() => filtersFromSearch(location.search), [location.search]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showSources, setShowSources] = useState(false);
  const [search, setSearch] = useState(filters.q ?? '');

  const me = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: async () => (await adminFetch<{ admin: AdminMe }>('/api/admin/auth/me')).admin,
  });
  const canAct = adminCan(me.data, 'alerts.write');
  const alerts = useQuery({
    queryKey: ['admin', 'alerts', buildQuery(filters)],
    queryFn: () => adminFetch<AlertsResponse>(`/api/admin/alerts?${buildQuery(filters)}`),
    refetchInterval: 60_000,
  });
  const myProfile = useQuery({
    queryKey: ['admin', 'me', 'profile'],
    queryFn: () => adminFetch<MeProfile>('/api/admin/me/profile'),
    retry: false,
  });
  const savedViews: SavedAlertView[] = myProfile.data?.saved_alert_views ?? [];
  const [naming, setNaming] = useState(false);
  const [viewName, setViewName] = useState('');
  const saveView = async () => {
    const problem = viewNameError(viewName, savedViews);
    if (problem) {
      setActionError(problem);
      return;
    }
    try {
      await adminMutate('/api/admin/me/alert-views', 'PUT', {
        views: [...savedViews, { name: viewName.trim(), query: buildQuery(filters) }],
      });
      setNaming(false);
      setViewName('');
      setActionError(null);
      await myProfile.refetch();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const assignees = useQuery({
    queryKey: ['admin', 'alerts', 'assignees'],
    queryFn: async () => (await adminFetch<{ assignees: Assignee[] }>('/api/admin/alerts/assignees')).assignees,
  });

  const setFilters = (next: Partial<Filters>) => {
    const merged: Filters = { ...filters, ...next };
    setSelected(new Set());
    navigate({ search: `?${buildQuery(merged)}` }, { replace: true });
  };

  const rows = alerts.data?.alerts ?? [];
  const counts = alerts.data?.counts;
  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));

  const act = async (action: string, extra: { assignee_admin_id?: string | null; note?: string } = {}, only?: AlertItem) => {
    const ids = only ? (only.member_ids.length ? only.member_ids : [only.id]) : memberIds(rows, selected);
    if (!ids.length) return;
    setBusy(true);
    setActionError(null);
    try {
      await adminMutate('/api/admin/alerts/actions', 'POST', { ids, action, ...extra });
      setSelected(new Set());
      await alerts.refetch();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const viewCount = (v: View): number | undefined => {
    if (!counts) return undefined;
    if (v === 'needs_attention') return counts.needs_attention;
    if (v === 'critical') return counts.critical_open;
    if (v === 'mine') return counts.mine_open;
    if (v === 'open') return counts.by_status.new + counts.by_status.acknowledged;
    return undefined;
  };

  const failing = (alerts.data?.sources ?? []).filter(s => !s.ok);

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
          <BellRing className="h-5 w-5" style={{ color: '#1F6C4F' }} /> Alerts
        </h1>
        <div className="flex items-center gap-2">
          <select
            value={filters.since_days ?? 30}
            onChange={e => setFilters({ since_days: parseInt(e.target.value, 10) })}
            className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700"
            aria-label="Time window"
          >
            {[1, 7, 30, 90].map(d => <option key={d} value={d}>Last {d} {d === 1 ? 'day' : 'days'}</option>)}
          </select>
          <button
            type="button"
            onClick={() => alerts.refetch()}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className={`h-3 w-3 ${alerts.isFetching ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>
      <p className="mt-1 text-sm text-gray-500">
        Every alert the platform writes, from every source. Repeats of the same alert are grouped.
      </p>

      {failing.length > 0 && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            {failing.length === 1 ? 'One source' : `${failing.length} sources`} couldn't be read, so this list may be
            incomplete: {failing.map(s => `${s.label} (${s.error})`).join('; ')}.
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-1 border-b border-gray-200">
        {VIEW_ORDER.map(v => {
          const n = viewCount(v);
          const active = filters.view === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => setFilters({ view: v })}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${active ? 'font-semibold' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              style={active ? { borderColor: '#1F6C4F', color: '#1F6C4F' } : {}}
            >
              {VIEW_LABELS[v]}
              {n !== undefined && n > 0 && (
                <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700">{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {(savedViews.length > 0 || naming) && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs">
          <Bookmark className="h-3 w-3 text-gray-400" />
          {savedViews.map(v => (
            <button key={v.name} type="button"
              onClick={() => navigate({ search: `?${v.query}` }, { replace: true })}
              className={`rounded-full px-2 py-0.5 ${buildQuery(filters) === v.query ? 'bg-[#1F6C4F] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {v.name}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select value={filters.area ?? ''} onChange={e => setFilters({ area: e.target.value || undefined })}
          className="rounded-md border border-gray-200 px-2 py-1 text-xs" aria-label="Area">
          <option value="">All areas</option>
          {(alerts.data?.areas ?? []).map(a => (
            <option key={a.key} value={a.key}>
              {a.label}{counts?.open_by_area[a.key] ? ` (${counts.open_by_area[a.key]})` : ''}
            </option>
          ))}
        </select>
        <select value={filters.source ?? ''} onChange={e => setFilters({ source: e.target.value || undefined })}
          className="rounded-md border border-gray-200 px-2 py-1 text-xs" aria-label="Source">
          <option value="">All sources</option>
          {(alerts.data?.sources ?? []).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={filters.severity ?? ''} onChange={e => setFilters({ severity: e.target.value || undefined })}
          className="rounded-md border border-gray-200 px-2 py-1 text-xs" aria-label="Severity">
          <option value="">Any severity</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>
        <select value={filters.status ?? ''} onChange={e => setFilters({ status: e.target.value || undefined })}
          className="rounded-md border border-gray-200 px-2 py-1 text-xs" aria-label="Status">
          <option value="">Any status</option>
          {(Object.keys(STATUS_LABELS) as (keyof typeof STATUS_LABELS)[]).map(s => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
        <select value={filters.assignee ?? ''} onChange={e => setFilters({ assignee: e.target.value || undefined })}
          className="rounded-md border border-gray-200 px-2 py-1 text-xs" aria-label="Assignee">
          <option value="">Anyone</option>
          <option value="none">Unassigned</option>
          {(assignees.data ?? []).map(a => <option key={a.id} value={a.id}>{a.full_name || a.email}</option>)}
        </select>
        <form
          onSubmit={e => { e.preventDefault(); setFilters({ q: search || undefined }); }}
          className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1"
        >
          <Search className="h-3 w-3 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search"
            className="w-40 text-xs outline-none" aria-label="Search alerts" />
        </form>
        {myProfile.data && (naming ? (
          <span className="inline-flex items-center gap-1 text-xs">
            <input value={viewName} onChange={e => setViewName(e.target.value)} maxLength={60} autoFocus
              placeholder="Name this view" className="w-36 rounded-md border border-gray-200 px-2 py-1" aria-label="View name" />
            <button type="button" onClick={saveView} className="rounded-md px-2 py-1 font-medium text-white" style={{ backgroundColor: '#1F6C4F' }}>Save</button>
            <button type="button" onClick={() => { setNaming(false); setViewName(''); }} className="rounded-md border border-gray-200 px-2 py-1">Cancel</button>
          </span>
        ) : (
          <button type="button" onClick={() => setNaming(true)}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">
            <Bookmark className="h-3 w-3" /> Save view
          </button>
        ))}
      </div>

      {canAct && selected.size > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-gray-50 p-2 text-xs">
          <span className="font-medium text-gray-700">{selected.size} selected</span>
          <button type="button" disabled={busy} onClick={() => act('acknowledge')}
            className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 hover:bg-gray-50">
            <Check className="h-3 w-3" /> Acknowledge
          </button>
          <button type="button" disabled={busy} onClick={() => act('resolve')}
            className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 hover:bg-gray-50">
            <Check className="h-3 w-3" /> Resolve
          </button>
          <button type="button" disabled={busy} onClick={() => act('ignore')}
            className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 hover:bg-gray-50">
            <CircleSlash className="h-3 w-3" /> Ignore
          </button>
          <button type="button" disabled={busy} onClick={() => act('reopen')}
            className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 hover:bg-gray-50">
            <RotateCcw className="h-3 w-3" /> Reopen
          </button>
          <label className="inline-flex items-center gap-1">
            <UserPlus className="h-3 w-3" />
            <select disabled={busy} value="" onChange={e => act('assign', { assignee_admin_id: e.target.value === 'nobody' ? null : e.target.value })}
              className="rounded border border-gray-200 bg-white px-1 py-1" aria-label="Assign selected">
              <option value="">Assign to…</option>
              <option value="nobody">Nobody</option>
              {(assignees.data ?? []).map(a => <option key={a.id} value={a.id}>{a.full_name || a.email}</option>)}
            </select>
          </label>
        </div>
      )}
      {actionError && <div className="mt-2 text-xs text-red-600" role="alert">{actionError}</div>}

      {alerts.isLoading && <div className="mt-6 text-sm text-gray-500">Loading alerts…</div>}
      {alerts.error && <div className="mt-6 rounded-md bg-red-50 p-3 text-sm text-red-700">{errorMessage(alerts.error)}</div>}

      {alerts.data && rows.length === 0 && (
        <div className="mt-8 rounded-lg border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
          Nothing here. {filters.view === 'needs_attention' ? 'No new warnings or critical alerts.' : 'No alerts match these filters.'}
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="w-8 px-3 py-2">
                  {canAct && (
                    <input type="checkbox" checked={allSelected} aria-label="Select all"
                      onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map(r => r.id)))} />
                  )}
                </th>
                <th className="px-3 py-2">Alert</th>
                <th className="px-3 py-2">Area</th>
                <th className="px-3 py-2">Last seen</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Assignee</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const open = expanded === r.id;
                const area = alerts.data?.areas.find(a => a.key === r.area)?.label ?? r.area;
                const sourceLabel = alerts.data?.sources.find(s => s.key === r.source)?.label ?? r.source;
                return (
                  <React.Fragment key={r.id}>
                    <tr className={`border-l-4 ${SEVERITY_STYLE[r.severity].border} border-t border-gray-100 align-top hover:bg-gray-50`}>
                      <td className="px-3 py-2">
                        {canAct && (
                          <input type="checkbox" checked={selected.has(r.id)} aria-label={`Select ${r.title}`}
                            onChange={() => {
                              const next = new Set(selected);
                              if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                              setSelected(next);
                            }} />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <button type="button" onClick={() => setExpanded(open ? null : r.id)} className="flex items-start gap-1 text-left">
                          {open ? <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                          <span>
                            <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${SEVERITY_STYLE[r.severity].pill}`}>{r.severity}</span>
                            <span className="font-medium text-gray-900">{r.title}</span>
                            {r.occurrences > 1 && <span className="ml-2 text-xs text-gray-500">×{r.occurrences}</span>}
                            <span className="mt-0.5 block text-xs text-gray-400">{sourceLabel}</span>
                          </span>
                        </button>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{area}</td>
                      <td className="px-3 py-2 text-xs text-gray-600" title={r.last_seen_at ?? ''}>{ago(r.last_seen_at)}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{STATUS_LABELS[r.status]}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{r.assignee_email ?? '—'}</td>
                    </tr>
                    {open && (
                      <tr className="border-t border-gray-100 bg-gray-50">
                        <td />
                        <td colSpan={5} className="px-3 py-3 text-xs text-gray-700">
                          {r.message && <p className="whitespace-pre-wrap">{r.message}</p>}
                          <p className="mt-2 text-gray-500">
                            First seen {ago(r.first_seen_at)} · last seen {ago(r.last_seen_at)}
                            {r.occurrences > 1 ? ` · ${r.occurrences} occurrences` : ''}
                            {r.alert_type ? ` · type ${r.alert_type}` : ''}
                          </p>
                          {r.note && (
                            <p className="mt-2 rounded bg-white p-2"><MessageSquare className="mr-1 inline h-3 w-3" />{r.note}</p>
                          )}
                          {r.state_updated_by && (
                            <p className="mt-1 text-gray-400">Last changed by {r.state_updated_by} {ago(r.state_updated_at)}</p>
                          )}
                          {r.details != null && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-gray-500">Details</summary>
                              <pre className="mt-1 max-h-64 overflow-auto rounded bg-white p-2 text-[11px]">{JSON.stringify(r.details, null, 2)}</pre>
                            </details>
                          )}
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            {canAct && r.status !== 'acknowledged' && r.status !== 'resolved' && (
                              <button type="button" disabled={busy} onClick={() => act('acknowledge', {}, r)}
                                className="rounded border border-gray-200 bg-white px-2 py-1 hover:bg-gray-50">Acknowledge</button>
                            )}
                            {canAct && r.status !== 'resolved' && (
                              <button type="button" disabled={busy} onClick={() => act('resolve', {}, r)}
                                className="rounded border border-gray-200 bg-white px-2 py-1 hover:bg-gray-50">Resolve</button>
                            )}
                            {canAct && (r.status === 'resolved' || r.status === 'ignored') && (
                              <button type="button" disabled={busy} onClick={() => act('reopen', {}, r)}
                                className="rounded border border-gray-200 bg-white px-2 py-1 hover:bg-gray-50">Reopen</button>
                            )}
                            {canAct && (
                              <NoteButton disabled={busy} onSave={note => act('note', { note }, r)} />
                            )}
                            {r.link && (
                              <Link to={r.link} className="inline-flex items-center gap-1 text-[#1F6C4F] hover:underline">
                                Open in its page <ExternalLink className="h-3 w-3" />
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {alerts.data?.truncated && (
            <div className="border-t border-gray-100 p-2 text-center text-xs text-gray-500">
              Showing the newest {rows.length} of {alerts.data.total}. Narrow the filters to see the rest.
            </div>
          )}
        </div>
      )}

      <div className="mt-6">
        <button type="button" onClick={() => setShowSources(!showSources)}
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {showSources ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <Database className="h-3 w-3" /> Sources ({alerts.data?.sources.length ?? 0})
        </button>
        {showSources && alerts.data && (
          <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-left uppercase tracking-wide text-gray-500">
                <tr><th className="px-3 py-2">Source</th><th className="px-3 py-2">In window</th><th className="px-3 py-2">Last written</th><th className="px-3 py-2">State</th></tr>
              </thead>
              <tbody>
                {alerts.data.sources.map(s => (
                  <tr key={s.key} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{s.label}</div>
                      {s.note && <div className="text-gray-400">{s.note}</div>}
                    </td>
                    <td className="px-3 py-2">{s.rows}</td>
                    <td className="px-3 py-2" title={s.last_written_at ?? ''}>{ago(s.last_written_at)}</td>
                    <td className="px-3 py-2">
                      {!s.ok ? <span className="text-red-600">Can't read: {s.error}</span>
                        : isQuiet(s) ? <span className="text-amber-700">Quiet for 14+ days</span>
                        : <span className="text-green-700">OK</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const NoteButton: React.FC<{ disabled: boolean; onSave: (note: string) => void }> = ({ disabled, onSave }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  if (!open) {
    return (
      <button type="button" disabled={disabled} onClick={() => setOpen(true)}
        className="rounded border border-gray-200 bg-white px-2 py-1 hover:bg-gray-50">Add note</button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input value={text} onChange={e => setText(e.target.value)} maxLength={2000} autoFocus
        className="w-64 rounded border border-gray-300 px-2 py-1" aria-label="Note" />
      <button type="button" disabled={disabled || !text.trim()} onClick={() => { onSave(text.trim()); setOpen(false); setText(''); }}
        className="rounded px-2 py-1 font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#1F6C4F' }}>Save</button>
      <button type="button" onClick={() => setOpen(false)} className="rounded border border-gray-200 bg-white px-2 py-1">Cancel</button>
    </span>
  );
};

const AdminAlerts: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <AlertsInner />
  </QueryClientProvider>
);

export default AdminAlerts;
